import { useCallback, useEffect, useState } from "react";
import { getCurrentMailItem, type MailItemSummary } from "../lib/office";
import { authenticate, getAuthState, type AuthState } from "../lib/auth";
import { fetchAutoMatch, type CandidateCase, type SearchResultCase, type MatchInput } from "../lib/cases";
import { CaseContext, type BoundCase } from "./components/CaseContext";
import { CasePicker } from "./components/CasePicker";
import { ChatPanel } from "./components/ChatPanel";

type MatchState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "matched"; auto: CandidateCase | null; hasCandidates: boolean }
  | { status: "error"; message: string };

export function App() {
  const [auth, setAuth] = useState<AuthState>(() => getAuthState());
  const [item, setItem] = useState<MailItemSummary | null>(null);
  const [matchState, setMatchState] = useState<MatchState>({ status: "idle" });
  const [bound, setBound] = useState<BoundCase | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);

  useEffect(() => {
    setItem(getCurrentMailItem());
  }, []);

  // Auto-match whenever we have an authenticated session AND a real item.
  // Bail-out when from is empty (Compose-without-recipient, or unsaved drafts).
  useEffect(() => {
    if (auth.status !== "authenticated") return;
    if (!item || !item.from) return;

    const ac = new AbortController();
    const input: MatchInput = {
      fromEmail: item.from,
      subject: item.subject || undefined,
    };

    setMatchState({ status: "loading" });
    fetchAutoMatch(input, ac.signal)
      .then((r) => {
        setMatchState({ status: "matched", auto: r.match, hasCandidates: r.hasCandidates });
        if (r.match) {
          setBound({ kind: "auto", case: r.match });
        }
      })
      .catch((e) => {
        if ((e as { name?: string }).name === "AbortError") return;
        setMatchState({ status: "error", message: e instanceof Error ? e.message : "Match mislukt" });
      });

    return () => ac.abort();
  }, [auth.status, item]);

  const handleSignIn = useCallback(async () => {
    setSignInError(null);
    try {
      const next = await authenticate();
      setAuth(next);
    } catch (e) {
      setSignInError(e instanceof Error ? e.message : "Onbekende fout bij inloggen");
    }
  }, []);

  const handleSelect = useCallback((selected: CandidateCase | SearchResultCase) => {
    setBound({ kind: "manual", case: selected });
    setPickerOpen(false);
  }, []);

  const matchInputForPicker: MatchInput | null = item?.from ? {
    fromEmail: item.from,
    subject: item.subject || undefined,
  } : null;

  return (
    <div className="flex flex-col h-full relative">
      <header className="px-4 py-3 border-b border-chrome-border flex items-center gap-2">
        <div className="w-6 h-6 rounded bg-accent flex items-center justify-center text-white text-xs font-semibold">C</div>
        <h1 className="text-sm font-semibold">Casemeister</h1>
        {auth.status === "authenticated" && (
          <span className="ml-auto text-xs text-chrome-subtle truncate max-w-[140px]">{auth.user.email}</span>
        )}
      </header>

      <main className="flex-1 overflow-y-auto p-4 space-y-4">
        {auth.status === "unauthenticated" && (
          <div className="space-y-3">
            <p className="text-sm text-chrome-subtle">
              Log in met je Microsoft 365 account om dossier-context te zien en met Casey te antwoorden.
            </p>
            <button
              type="button"
              onClick={handleSignIn}
              className="w-full px-4 py-2 bg-accent hover:bg-accent-hover text-white text-sm font-medium rounded transition-colors"
            >
              Inloggen met Microsoft 365
            </button>
            {signInError && <p className="text-xs text-red-600">{signInError}</p>}
          </div>
        )}

        {auth.status === "authenticating" && (
          <p className="text-sm text-chrome-subtle">Bezig met inloggen…</p>
        )}

        {auth.status === "authenticated" && (
          <>
            <section className="space-y-2">
              <div className="text-xs uppercase tracking-wide text-chrome-subtle">Geselecteerde mail</div>
              {item && item.from ? (
                <div className="rounded border border-chrome-border bg-white p-3 space-y-1">
                  <div className="text-sm font-medium truncate" title={item.subject}>
                    {item.subject || "(geen onderwerp)"}
                  </div>
                  <div className="text-xs text-chrome-subtle truncate" title={item.from}>{item.from}</div>
                </div>
              ) : (
                <p className="text-sm text-chrome-subtle">Selecteer een mail om context te laden.</p>
              )}
            </section>

            <section className="space-y-2">
              <div className="text-xs uppercase tracking-wide text-chrome-subtle">Dossier</div>
              {matchState.status === "loading" && (
                <div className="rounded border border-chrome-border bg-white p-3">
                  <p className="text-sm text-chrome-subtle">Dossier zoeken…</p>
                </div>
              )}
              {matchState.status === "error" && (
                <div className="rounded border border-red-200 bg-red-50 p-3">
                  <p className="text-xs text-red-700">{matchState.message}</p>
                </div>
              )}
              {(matchState.status === "matched" || matchState.status === "idle") && (
                <CaseContext bound={bound} onChangeRequest={() => setPickerOpen(true)} />
              )}
            </section>

            {bound && item && (
              <ChatPanel caseRef={bound.case} mailItem={item} />
            )}

            <div className="rounded border border-dashed border-chrome-border p-3 text-xs text-chrome-subtle">
              Sprint 3 — Casey-draft. Folder-finalize (sprint 4) en observability (sprint 5) komen nog.
            </div>
          </>
        )}
      </main>

      {pickerOpen && matchInputForPicker && (
        <CasePicker
          matchInput={matchInputForPicker}
          onSelect={handleSelect}
          onCancel={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}
